/**
 * The deck that sits on the felt of a card game. A real table shows its shoe:
 * how much is left is public information, and the stack shrinking through the
 * night is half the reason the cut card means anything. Roulette and craps
 * deal no cards, so they get nothing.
 */
import type { GameType, RoomState } from '@tv-casino/shared';

export interface DeckShoeVM {
  /** What the plate under the stack calls it — a shoe holds more than a deck. */
  label: string;
  /** The second line: cards left, or the hand number when the deck is reshuffled every hand. */
  note: string;
  /** Fraction of a full shoe still to deal, 0–1. The stack's thickness. */
  depth: number;
  /** Card backs drawn in the stack. Thickness, not a card count. */
  layers: number;
  /** At or past the cut card: the next round deals from a fresh shoe. */
  low: boolean;
  /** Screen-reader line, since the stack itself is decoration. */
  ariaLabel: string;
}

/** The games that deal cards. Roulette and craps never show a deck. */
const CARD_GAMES: readonly GameType[] = ['blackjack', 'baccarat', 'holdem', 'threecard'];

/** Card backs in a full stack. A 312-card shoe is drawn, not counted. */
const MAX_LAYERS = 14;

/** Thickness for a depth: anything still in the shoe reads as more than one card. */
export const shoeLayers = (depth: number): number =>
  depth <= 0 ? 0 : Math.min(MAX_LAYERS, Math.max(2, Math.round(depth * MAX_LAYERS)));

export function deckShoe(state: RoomState | null): DeckShoeVM | null {
  if (!state || !CARD_GAMES.includes(state.gameType) || state.shoeSize <= 0) return null;
  const remaining = Math.max(0, Math.min(state.shoeRemaining, state.shoeSize));
  // The server only stacks a shoe on the first deal, and a live one is rebuilt
  // at the cut card rather than run to nothing — so an empty count means the
  // cards are waiting in the holder, and the shoe is drawn full.
  const stacked = remaining > 0;
  const depth = stacked ? remaining / state.shoeSize : 1;
  // Hold'em shuffles a fresh deck every hand, so it has no cut card to run
  // down to and its count says nothing about the night — it prints the hand.
  const holdem = state.gameType === 'holdem';
  const penetration =
    state.gameType === 'baccarat' ? state.baccaratRules.penetration : state.rules.penetration;
  const label = holdem ? 'Deck' : 'Shoe';
  return {
    label,
    note: !stacked ? 'Ready' : holdem ? `Hand · ${state.round}` : `${remaining} cards`,
    depth,
    layers: shoeLayers(depth),
    low: stacked && !holdem && remaining <= state.shoeSize * penetration,
    ariaLabel: stacked
      ? `${label}: ${remaining} of ${state.shoeSize} cards left`
      : `${label}: ${state.shoeSize} cards, ready to deal`,
  };
}
